/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import classnames from 'classnames';
import { Module, Button } from '@msdyn365-commerce-modules/utilities';
import { IVinhusetNavigationMenuViewProps } from './vinhuset-navigation-menu';

function clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
}

export default (props: IVinhusetNavigationMenuViewProps) => {
    const { baseClassName, productMenuItem } = props;

    const [productsDropDownOpen, setProductsDropDownOpen] = React.useState(false);
    const [hasMultiRowClass, setHasMultiRowClass] = React.useState(false);
    const [dropdownStart, setDropdownStart] = React.useState<number | undefined>(undefined);
    const [showDropdown, setShowDropdown] = React.useState(false);

    const listRef = React.useRef<HTMLUListElement>(null);

    const menuDropdownRef = React.useRef<HTMLUListElement>(null);
    const menuDropdownButtonRef = React.useRef<HTMLButtonElement>(null);

    const productsDropdownLinkRef = React.useRef<HTMLAnchorElement>(null);
    const productsDropdownRef = React.useRef<HTMLUListElement>(null);

    const onClickHandler = (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        setProductsDropDownOpen(!productsDropDownOpen);
    };

    React.useEffect(() => {
        // When clicking outside the dropdown, close it
        const handleClick = (event: MouseEvent) => {
            if (productsDropdownLinkRef.current && productsDropdownRef.current && productsDropdownLinkRef.current !== event.target && !productsDropdownRef.current.contains(event.target as Node)) {
                setProductsDropDownOpen(false);
            }
        };
        document.addEventListener('click', handleClick);
    }, []);

    React.useEffect(() => {
        const onResize = () => {
            if (!listRef.current || listRef.current.children.length <= 1) {
                return;
            }

            const firstOffsetTop = (listRef.current.children[0] as HTMLElement).offsetTop;

            for (let i = 0; i < listRef.current.children.length; i++) {
                const element = listRef.current.children[i] as HTMLElement;
                if (element.offsetTop !== firstOffsetTop) {
                    setDropdownStart(i);
                    setHasMultiRowClass(true);
                    return;
                }
            }

            setDropdownStart(undefined);
            setHasMultiRowClass(false);
        };

        onResize();

        let observer: ResizeObserver | undefined;

        if (ResizeObserver) {
            observer = new ResizeObserver(() => {
                onResize();
            });
            observer.observe(listRef.current as Element);
        } else {
            console.warn('ResizeObserver not supported, using window resize event instead');
            window.addEventListener('resize', onResize);
        }

        return () => {
            if (observer) {
                observer.disconnect();
            } else {
                window.removeEventListener('resize', onResize);
            }
        };
    }, []);

    React.useEffect(() => {
        const onClickOutside = (event: MouseEvent) => {
            if (menuDropdownRef.current && menuDropdownButtonRef.current) {
                if (!menuDropdownRef.current.contains(event.target as Node)
                    && menuDropdownButtonRef.current !== event.target
                    && !menuDropdownButtonRef.current.contains(event.target as Node)
                ) {
                    setShowDropdown(false);
                }
            }
        };

        document.addEventListener('click', onClickOutside);

        return () => {
            document.removeEventListener('click', onClickOutside);
        };
    }, []);

    const menuItems = props.menuItems.map((item, index) => {
        if (item.title === '<SEPARATOR>') {
            return null;
            // We don't support separator in main menu for now because of overflow menu and being unsure how to handle it
            // return <li key={index} className={`${baseClassName}__list__separator`} />;
        }

        return (
            <li key={index} className={`${baseClassName}__list__item`}>
                <a className={`${baseClassName}__list__item__link`} href={item.url}>{item.title}</a>
            </li>
        );
    });

    if (productMenuItem.children
        && productMenuItem.children.length > 0
        && (productMenuItem.showUnauthorized || props.context.request.user.isAuthenticated)
    ) {
        const productsMenuItemPosition = props.config.productsMenuItemPosition || 1;
        const productMenuItemIndex = clamp((productsMenuItemPosition - 1), 0, props.menuItems.length); // Minus one the config value so it's zero-based
        // Insert products menu item
        menuItems.splice(productMenuItemIndex, 0, (
            <li key='ProductsMenu' className={`${baseClassName}__list__item dropdown`}>
                <a className={`${baseClassName}__list__item__link`} ref={productsDropdownLinkRef} href='#' onClick={onClickHandler}>{productMenuItem.title}</a>
                <ul className={`${baseClassName}__list__item__sub-list`} ref={productsDropdownRef} data-open={productsDropDownOpen}>
                    {productMenuItem.children && productMenuItem.children.map((child, childIndex) => {
                        if (child.title === '<SEPARATOR>') {
                            return <li key={childIndex} className={`${baseClassName}__list__item__sub-list__separator`} />;
                        }

                        return (
                            <li key={childIndex} className={`${baseClassName}__list__item__sub-list__item`}>
                                <a href={child.url || '#'}>{child.title}</a>
                            </li>
                        );
                    })}
                </ul>
            </li>
        ));
    }

    const [documentLoaded, setDocumentLoaded] = React.useState(false);

    React.useEffect(() => {
        setDocumentLoaded(true);
    }, []);

    return (
        <Module {...props.navigationMenu}>
            <ul
                className={classnames(`${baseClassName}__list`, { 'multi-row': hasMultiRowClass })}
                ref={listRef}
                style={!documentLoaded ? { overflow: 'hidden' } : undefined} // Hide overflow on SSR to prevent layout shift
            >
                {menuItems.map((item, index) => {
                    if (!item || dropdownStart === undefined || index < dropdownStart) {
                        return item;
                    }

                    // Add drop class to items that should be in the dropdown
                    return React.cloneElement(item, { ...item.props, className: `${item.props.className} drop` });
                })}
            </ul>
            {dropdownStart !== undefined && dropdownStart > 0 && (
                <>
                    <div
                        onMouseOver={() => {
                            setShowDropdown(true);
                        }}
                        onMouseLeave={() => {
                            setShowDropdown(false);
                        }}
                        style={{ position: 'relative' }}
                    >
                        <Button
                            className={classnames(`${baseClassName}__list__dropdown__button`, { open: showDropdown })}
                            innerRef={menuDropdownButtonRef}
                            onClick={() => {
                                setShowDropdown(true);
                            }}
                        >
                            {props.resources.navigationOverflowButtonLabel}
                        </Button>
                        <ul className={classnames(`${baseClassName}__list__dropdown`, { show: showDropdown })} ref={menuDropdownRef}>
                            {menuItems.slice(dropdownStart)}
                        </ul>
                    </div>
                </>
            )}
        </Module>
    );
};
